import React, { Fragment, useEffect, useRef } from "react";
import styled from "styled-components";
import ChevronRight from "@material-ui/icons/ChevronRight";
import { last } from "lodash";
import { ButtonGrid } from "@sonder/ui/source/styled/fragments/ButtonGrid";
import { Link } from "@sonder/ui/source/styled/fragments/Link";
import {
  Scroller,
  ScrollerContent,
} from "@sonder/ui/source/styled/fragments/Scroller";
import { colors } from "@sonder/ui/source/styled/theme";
import { useSegment } from "analytics/segment";
import { GetTaskCategoriesCategory } from "core/queries/GetTaskCategories";
import { GetTaskLocationsLocation } from "core/queries/GetTaskLocations";
import { TaskCategories } from "tasks/components/TaskCategories";
import {
  SerializedTreeStructureTaskContext,
  TaskContexts,
} from "tasks/components/TaskContexts";
import { TaskLocations } from "tasks/components/TaskLocations";
import { parseGraphQLId } from "tasks/helpers";
import { TaskContextSubjectTypeEnum, TypeEnum } from "typings/globalGQLTypes";

const StyledScroller = styled(Scroller)`
  margin: 0 -1.71rem;

  + ${ButtonGrid} {
    margin-top: 0.5rem;
  }
`;

const ScrollerInnerContents = styled.div`
  display: flex;
  padding: 0.5rem 1.71rem;
  white-space: nowrap;
`;
interface TaskGuidedFlowProps {
  guidedFlowState: GuidedFlowState;
  onGuidedFlowChange: (flow: GuidedFlowState) => void;
  onGuidedFlowClose?: () => void;
  subjectType: TaskContextSubjectTypeEnum;
  taskType: TypeEnum;
}

export interface GuidedFlowState {
  category: GetTaskCategoriesCategory | null;
  location: GetTaskLocationsLocation | null;
  contextNodes: SerializedTreeStructureTaskContext[];
}

export const initialGuidedFlowState = {
  category: null,
  contextNodes: [],
  location: null,
};

export const isTerminatingContextNode = (
  contextNode?: SerializedTreeStructureTaskContext | null,
) => contextNode?.children.length === 0 && Boolean(contextNode?.contextId);

export const TaskGuidedFlow = ({
  guidedFlowState,
  onGuidedFlowChange,
  onGuidedFlowClose,
  subjectType,
  taskType,
}: TaskGuidedFlowProps) => {
  const stepsRef = useRef<HTMLDivElement>(null);
  const stepsContentRef = useRef<HTMLDivElement>(null);
  const { category, contextNodes, location } = guidedFlowState;
  const currentContextNode = last(contextNodes);
  const isTerminatingNode = isTerminatingContextNode(currentContextNode);
  const { track } = useSegment();
  useEffect(() => {
    stepsRef.current?.scrollTo(stepsContentRef.current?.offsetWidth || 0, 0);
  }, [
    category?.id,
    location?.id,
    contextNodes.map(node => node.contextItemName).join(","),
  ]);
  const isLocationCollapsed = Boolean(location);
  const isCategoryCollapsed = !isLocationCollapsed || Boolean(category);
  const isContextCollapsed =
    !isLocationCollapsed || !isCategoryCollapsed || isTerminatingNode;
  const locationId = location?.id ? parseGraphQLId(location?.id) : null;
  const steps = [
    {
      name: "Search",
      onClick: () => {
        if (onGuidedFlowClose) onGuidedFlowClose();
      },
    },
    isLocationCollapsed
      ? {
          name: guidedFlowState.location?.name,
          onClick: () => {
            onGuidedFlowChange(initialGuidedFlowState);
          },
        }
      : null,
    isLocationCollapsed && isCategoryCollapsed
      ? {
          name: category?.name,
          onClick: () => {
            onGuidedFlowChange({
              ...guidedFlowState,
              category: initialGuidedFlowState.category,
              contextNodes: initialGuidedFlowState.contextNodes,
            });
          },
        }
      : null,
    ...contextNodes.map((node, index) => ({
      ...node,
      name: node.contextItemNameLocalized,
      onClick: () =>
        onGuidedFlowChange({
          ...guidedFlowState,
          contextNodes: contextNodes.slice(0, index),
        }),
    })),
    // Some Fallback Hackery!
    !isLocationCollapsed
      ? {
          name: "Location",
        }
      : null,
    !isCategoryCollapsed
      ? {
          name: "Category",
        }
      : null,
    !isContextCollapsed
      ? {
          name: "Context",
        }
      : null,
  ]
    .filter(x => Boolean(x))
    .map((step, index) => {
      return (
        <Fragment key={step?.name}>
          {index > 0 && <ChevronRight fontSize="small" />}
          {step?.onClick ? (
            <Link
              title="Clear"
              onClick={() => {
                step?.onClick();
              }}
            >
              {step?.name}
            </Link>
          ) : (
            <span>{step?.name}</span>
          )}
        </Fragment>
      );
    });
  return (
    <>
      {steps.length > 0 && (
        <StyledScroller backgroundColor={colors.grayF} ref={stepsRef}>
          <ScrollerContent ref={stepsContentRef}>
            <ScrollerInnerContents>{steps}</ScrollerInnerContents>
          </ScrollerContent>
        </StyledScroller>
      )}
      {!isLocationCollapsed && (
        <TaskLocations
          subjectType={subjectType}
          taskType={taskType}
          value={location}
          onChange={newLocation => {
            track({
              eventName: "task_guided_flow",
              action: "click",
              element: "location",
              props: { locationId: newLocation.id },
            });
            onGuidedFlowChange({ ...guidedFlowState, location: newLocation });
          }}
        />
      )}
      {!isCategoryCollapsed && (
        <TaskCategories
          locationId={locationId}
          subjectType={subjectType}
          taskType={taskType}
          value={category}
          onChange={newCategory => {
            track({
              eventName: "task_guided_flow",
              action: "click",
              element: "category",
              props: { categoryId: newCategory.id },
            });
            onGuidedFlowChange({ ...guidedFlowState, category: newCategory });
          }}
        />
      )}
      {!isContextCollapsed && (
        <TaskContexts
          categoryId={category?.id}
          locationId={locationId || undefined}
          subjectType={subjectType}
          taskContextNodes={currentContextNode && currentContextNode.children}
          taskType={taskType}
          onChange={context => {
            track({
              eventName: "task_guided_flow",
              action: "click",
              element: "context",
              props: { contextId: context.contextId },
            });
            onGuidedFlowChange({
              ...guidedFlowState,
              contextNodes: contextNodes.concat(context),
            });
          }}
        />
      )}
    </>
  );
};
